import { useState, useEffect } from "react";

import { BuilderComponent, builder, BuilderContent } from "@builder.io/react";
import { useRouter } from "next/router";
import "@/registry/builder-registry";
import { logger, FileNotFoundError } from "@orlandohealth/build-kit";

const Page = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [checkingRedirect, setCheckingRedirect] = useState(false);
  const router = useRouter();
  const [key, setKey] = useState(router.asPath);
  const [content, setContent] = useState<any>(null);

  const apiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY;
  if (apiKey) {
    builder.init(apiKey);
    builder.apiVersion = "v3";
  } else {
    logger.error("CMS API Key is not defined");
  }

  // Update key when route changes to force re-render
  useEffect(() => {
    setKey(router.asPath);
  }, [router.asPath]);

  // Correction logic: only runs if 404 page is rendered
  useEffect(() => {
    const handleBrokenLink = async () => {
      const { model, referenceId } = router.query;
      if (
        model &&
        referenceId &&
        typeof model === "string" &&
        typeof referenceId === "string"
      ) {
        setCheckingRedirect(true);
        try {
          const response = await fetch(
            `/api/content/${model}?fields=data.url&query=id:$eq:${referenceId}`
          );
          if (response.ok) {
            const data = await response.json();
            const correctUrl = Array.isArray(data)
              ? data[0]?.data?.url
              : data?.data?.url;
            if (correctUrl && correctUrl !== router.pathname) {
              router.replace(correctUrl);
              return;
            }
          }
        } catch (error) {
          logger.error("Error fetching content URL", "404", {
            error: String(error),
          });
        }
      }
      setCheckingRedirect(false);
    };
    handleBrokenLink();
  }, [router]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const page = await builder.get("page", {
          userAttributes: {
            urlPath: "/404",
          },
        });
        setContent(page);
        setIsLoading(false);
      } catch (error) {
        logger.error("Error fetching 404 content", "404", {
          error: String(error),
        });
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  const defaultErrorContent = {
    title: "Page Not Found - Orlando Health",
    description: "The page you are looking for cannot be found.",
    heading: "Oops!",
    subheading: key
      ? `We can't find the page \"${key}\"`
      : "We can't seem to find the page you're looking for.",
    errorCode: "Error code 404",
    helpfulLinks: "Here are some helpful links:",
    links: [
      { href: "/", text: "Home" },
      { href: "/locations", text: "Locations" },
      { href: "/physician-finder", text: "Find a Doctor" },
      { href: "/careers", text: "Careers" },
    ],
  };

  if (checkingRedirect || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinnerContainer">
          <div className="spinner"></div>
        </div>
        <span className="ml-2">
          {checkingRedirect ? "Attempting to find your page..." : "Loading..."}
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {!checkingRedirect && content ? (
        <BuilderComponent
          model="page"
          content={content}
          data={{
            path: key,
          }}
        />
      ) : (
        <FileNotFoundError
          heading={defaultErrorContent.heading}
          subheading={defaultErrorContent.subheading}
          errorCode={defaultErrorContent.errorCode}
          helpfulLinks={defaultErrorContent.helpfulLinks}
          links={defaultErrorContent.links}
          imageSrc="/images/404heart.png"
          imageAlt="404 Image"
        />
      )}
    </div>
  );
};

export default Page;
